import { useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import TextLink from 'LuxKit/TextLink'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import Clickable from 'components/Common/Clickable'

const CardContainer = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border-radius: ${rem(8)};
  overflow: hidden;
  align-self: stretch;
  box-shadow:
    0 ${rem(1)} ${rem(1.5)} rgba(0, 0, 0, 0.07),
    0 ${rem(1.5)} ${rem(5)} rgba(0, 0, 0, 0.05),
    0 ${rem(5)} ${rem(10)} rgba(0, 0, 0, 0.05),
    0 ${rem(15)} ${rem(32)} rgba(0, 0, 0, 0.04);

  ${mediaQueryUp.tablet} {
    max-width: ${rem(383)};
  }
`

const CardHeader = styled.div`
  padding: ${rem(14)} ${rem(16)};
  ${tierGradientBackgroundStyle}

  ${mediaQueryUp.desktop} {
    padding: ${rem(16)} ${rem(24)};
  }
`

const HeaderText = styled.span`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(16)};
  font-weight: ${props => props.theme.font.primary.weight.bold};
  line-height: ${rem(20)};
  ${tierNormalContrastColorStyle}

  ${mediaQueryUp.tablet} {
    font-size: ${rem(18)};
    line-height: ${rem(22)};
  }
`

const CardBody = styled.div`
  background: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(14)} ${rem(16)};
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};

  ${mediaQueryUp.desktop} {
    padding: ${rem(16)} ${rem(24)};
    gap: ${rem(10)};
  }
`

const HeaderToggle = styled.div`
  display: inline-flex;
  align-items: stretch;
  background: rgba(0, 0, 0, 0.15);
  border-radius: ${rem(6)};
  padding: ${rem(3)};
  gap: ${rem(2)};
`

const HeaderToggleOption = styled(Clickable)`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(13)};
  line-height: ${rem(18)};
  padding: ${rem(5)} ${rem(10)};
  border-radius: ${rem(4)};
  transition: background 150ms, opacity 150ms;
  ${tierNormalContrastColorStyle}

  &.is-active {
    font-weight: ${props => props.theme.font.primary.weight.bold};
    background: rgba(255, 255, 255, 0.3);
    opacity: 1;
  }

  &.is-inactive {
    font-weight: ${props => props.theme.font.primary.weight.regular};
    opacity: 0.7;
  }

  ${mediaQueryUp.tablet} {
    font-size: ${rem(14)};
    line-height: ${rem(20)};
    padding: ${rem(5)} ${rem(12)};
  }
`

const RequirementsText = styled.p`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(12)};
  font-weight: ${props => props.theme.font.primary.weight.regular};
  line-height: ${rem(16)};
  color: ${props => props.theme.palette.neutral.default.one};
  margin: 0;

  ${mediaQueryUp.tablet} {
    font-size: ${rem(14)};
    line-height: ${rem(20)};
  }
`

const BoldText = styled.span`
  font-weight: ${props => props.theme.font.primary.weight.bold};
`

const ViewDetailsLinkSmall = styled(TextLink)`
  && {
    display: inline-flex;
    margin-top: ${rem(4)};
    color: ${props => props.theme.palette.neutral.default.one};
  }

  ${mediaQueryUp.tablet} {
    && {
      display: none;
    }
  }
`

const ViewDetailsLinkMedium = styled(TextLink)`
  && {
    display: none;
    margin-top: ${rem(4)};
    color: ${props => props.theme.palette.neutral.default.one};
  }

  ${mediaQueryUp.tablet} {
    && {
      display: inline-flex;
    }
  }
`

const BenefitsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(6)};
  font-size: ${rem(12)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(8)};
    font-size: ${rem(14)};
  }
`

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(8)};
  }
`

const BenefitText = styled.span`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(12)};
  font-weight: ${props => props.theme.font.primary.weight.regular};
  line-height: ${rem(16)};
  color: ${props => props.theme.palette.neutral.default.one};

  ${mediaQueryUp.tablet} {
    font-size: ${rem(14)};
    line-height: ${rem(20)};
  }
`

const BenefitIconWrapper = styled.div`
  border-radius: ${props => props.theme.borderRadius.round};
  width: ${rem(20)};
  height: ${rem(20)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${mediaQueryUp.tablet} {
    width: ${rem(24)};
    height: ${rem(24)};
  }

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
  }

  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.darkBackground};
  }

  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.darkForeground};
  }

  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.darkBackground};
  }
`

interface TierBenefit {
  icon: React.ReactElement
  label: string
}

interface Props {
  /**
   * The tier to display benefits for
   */
  tier: App.LoyaltyTier
  /**
   * Whether this is for retaining current tier (true) or unlocking next tier (false)
   */
  isRetain?: boolean
  /**
   * Whether the member has already retained their platinum tier before the membership year ends
   * If true, the header text will be "Congratulations!"
   * If false, the header text will be "To retain [tierName]"
   */
  hasRetainedTier?: boolean
  creditsRemaining: number
  nightsRemaining: number
  expiryDate?: string
  onViewDetails?: () => void
  /**
   * Whether to show the retain/upgrade toggle (only for at-risk Silver/Gold members)
   */
  showToggle?: boolean
  /**
   * Whether the toggle is currently showing the upgrade view
   */
  showUpgradeView?: boolean
  /**
   * Called when the user switches between retain and upgrade views
   */
  onToggleView?: (showUpgrade: boolean) => void
  /**
   * Display name of the current tier (for the retain toggle option label)
   */
  currentTierName?: string
  /**
   * Display name of the next tier (for the upgrade toggle option label)
   */
  nextTierName?: string
}

function LoyaltyHeroUnlockTierCard({
  tier,
  isRetain = false,
  hasRetainedTier = false,
  creditsRemaining,
  nightsRemaining,
  expiryDate,
  onViewDetails,
  showToggle = false,
  showUpgradeView = false,
  onToggleView,
  currentTierName,
  nextTierName,
}: Props) {
  const intl = useIntl()
  const [loyaltyConfig] = useLoyaltyConfig()
  const tierName = getLoyaltyTierDisplayName(tier)

  const silverRoomUpgradeLabel = intl.formatMessage({
    id: 'eZtS3j26baFSBRXzUjue',
    defaultMessage: '1x free room or package upgrade',
    description: 'Silver tier benefit: number of free room or package upgrades',
  })
  const silverLoungePassLabel = intl.formatMessage({
    id: '4qFdUSk0ut7WMGkK8Isu',
    defaultMessage: '2x free airport lounge passes',
    description: 'Silver tier benefit: number of free airport lounge passes',
  })
  const silverInsuranceLabel = intl.formatMessage({
    id: 'T0LxmmCqgQpmaYM6EEtl',
    defaultMessage: '5% travel protection discount',
    description: 'Silver tier benefit: percentage discount on travel insurance',
  })
  const goldRoomUpgradeLabel = intl.formatMessage({
    id: 'wAfKl-BcPVMPtQfCWLWG',
    defaultMessage: '2x free room or package upgrades',
    description: 'Gold tier benefit: number of free room or package upgrades',
  })
  const goldLoungePassLabel = intl.formatMessage({
    id: 'OYnVWB_hAmzm9CF_YzUy',
    defaultMessage: '2x free airport lounge passes',
    description: 'Gold tier benefit: number of free airport lounge passes',
  })
  const goldTransferLabel = intl.formatMessage({
    id: 'myl5aY7sVx-kCzQdWbht',
    defaultMessage: '2x free airport chauffeur service',
    description: 'Gold tier benefit: number of free airport chauffeur transfers',
  })
  const platinumRoomUpgradeLabel = intl.formatMessage({
    id: 'xmDpDkzWNZK7z21rSPUh',
    defaultMessage: '4x free room or package upgrades',
    description: 'Platinum tier benefit: number of free room or package upgrades',
  })
  const platinumLoungePassLabel = intl.formatMessage({
    id: 'SlQqALxI58IltN__mqAC',
    defaultMessage: '2x free airport lounge passes',
    description: 'Platinum tier benefit: number of free airport lounge passes',
  })
  const platinumTransferLabel = intl.formatMessage({
    id: 'NCsDLxaDWFIY1ULFBqiS',
    defaultMessage: 'Unlimited free airport chauffeur service',
    description: 'Platinum tier benefit: unlimited free airport chauffeur transfers',
  })

  const benefits = useMemo(() => {
    if (!loyaltyConfig) return []
    const enabledBenefits = loyaltyConfig.regionEnabledBenefits
    const result: Array<TierBenefit> = []

    if (tier === 'silver') {
      if (enabledBenefits.hotel_upgrade) {
        result.push({ icon: <LineCrownIcon colour="neutral-eight" size={15} />, label: silverRoomUpgradeLabel })
      }
      if (enabledBenefits.lounge_pass) {
        result.push({ icon: <LineChairWithAirplaneIcon colour="neutral-eight" size={15} />, label: silverLoungePassLabel })
      }
      if (enabledBenefits.discounted_insurance) {
        result.push({ icon: <LineShieldCheckIcon colour="neutral-eight" size={15} />, label: silverInsuranceLabel })
      }
    } else if (tier === 'gold') {
      if (enabledBenefits.hotel_upgrade) {
        result.push({ icon: <LineCrownIcon colour="neutral-eight" size={15} />, label: goldRoomUpgradeLabel })
      }
      if (enabledBenefits.lounge_pass) {
        result.push({ icon: <LineChairWithAirplaneIcon colour="neutral-eight" size={15} />, label: goldLoungePassLabel })
      }
      if (enabledBenefits.airport_transfer) {
        result.push({ icon: <LineTaxiIcon colour="neutral-eight" size={15} />, label: goldTransferLabel })
      }
    } else if (tier === 'platinum') {
      if (enabledBenefits.hotel_upgrade) {
        result.push({ icon: <LineCrownIcon colour="neutral-eight" size={15} />, label: platinumRoomUpgradeLabel })
      }
      if (enabledBenefits.lounge_pass) {
        result.push({ icon: <LineChairWithAirplaneIcon colour="neutral-eight" size={15} />, label: platinumLoungePassLabel })
      }
      if (enabledBenefits.airport_transfer) {
        result.push({ icon: <LineTaxiIcon colour="neutral-eight" size={15} />, label: platinumTransferLabel })
      }
    }

    return result
  }, [tier, loyaltyConfig, silverRoomUpgradeLabel, silverLoungePassLabel, silverInsuranceLabel, goldRoomUpgradeLabel, goldLoungePassLabel, goldTransferLabel, platinumRoomUpgradeLabel, platinumLoungePassLabel, platinumTransferLabel])

  const hasCredits = creditsRemaining > 0
  const hasNights = nightsRemaining > 0

  // Determine header and body text based on retain status
  const congratulationsHeader = intl.formatMessage({
    id: 'BZdjhoiOJyIPmC3g2UVQ',
    defaultMessage: 'Congratulations!',
    description: 'Header shown when user has successfully retained their loyalty tier',
  })
  const toRetainHeader = intl.formatMessage({
    id: '_Qa0aQaYTBtMzj4n5pLA',
    defaultMessage: 'To retain {tierName}',
    description: 'Header indicating requirements to retain current loyalty tier status',
  }, { tierName })
  const toUnlockHeader = intl.formatMessage({
    id: 'LELHCBxctftbQv7ojORs',
    defaultMessage: 'To unlock {tierName}',
    description: 'Header indicating requirements to unlock next loyalty tier',
  }, { tierName })

  const formattedExpiryDate = expiryDate ? <FormatDateTime value={expiryDate} format="dmy-casual-short" /> : undefined
  const boldTag = (chunks: React.ReactNode) => <BoldText>{chunks}</BoldText>

  let headerText: string
  let bodyText: React.ReactNode

  if (hasRetainedTier) {
    headerText = congratulationsHeader
    bodyText = expiryDate
      ? (
          <FormattedMessage
            id="g_dKnpKiiuRMp7DcLyym"
            defaultMessage="You have retained {tierName} and can enjoy these benefits until {expiryDate}:"
            description="Body text when user has retained their loyalty tier, showing expiry date"
            values={{ tierName, expiryDate: formattedExpiryDate }}
          />
        )
      : (
          <FormattedMessage
            id="wm7aJ8GCXIBp63AyNkOm"
            defaultMessage="You have retained {tierName} and can enjoy these benefits:"
            description="Body text when user has retained their loyalty tier, no expiry"
            values={{ tierName }}
          />
        )
  } else if (isRetain) {
    headerText = toRetainHeader
    if (hasCredits && hasNights && expiryDate) {
      bodyText = <FormattedMessage
        id="DZCYP5AMkQDHF968Mw8l"
        defaultMessage="You need <bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold> and <bold>{nightsCount, plural, one {# Night} other {# Nights}}</bold> by {expiryDate} to retain these {tierName} benefits:"
        description="Retain tier: credits and nights needed with expiry date"
        values={{ creditsCount: creditsRemaining, nightsCount: nightsRemaining, expiryDate: formattedExpiryDate, tierName, bold: boldTag }}
      />
    } else if (hasCredits && hasNights) {
      bodyText = <FormattedMessage
        id="FEofmLq8VqTdW7E5VIR8"
        defaultMessage="You need <bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold> and <bold>{nightsCount, plural, one {# Night} other {# Nights}}</bold> to retain these {tierName} benefits:"
        description="Retain tier: credits and nights needed, no expiry in the loyalty program section"
        values={{ creditsCount: creditsRemaining, nightsCount: nightsRemaining, tierName, bold: boldTag }}
      />
    } else if (hasCredits && expiryDate) {
      bodyText = <FormattedMessage
        id="nyzAYxn-g0fQtJNnewC2"
        defaultMessage="You need <bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold> by {expiryDate} to retain these {tierName} benefits:"
        description="Retain tier: credits needed with expiry date in the loyalty section"
        values={{ creditsCount: creditsRemaining, expiryDate: formattedExpiryDate, tierName, bold: boldTag }}
      />
    } else if (hasCredits) {
      bodyText = <FormattedMessage
        id="oZ0DKKL1J-7kWD3f3qcm"
        defaultMessage="You need <bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold> to retain these {tierName} benefits:"
        description="Retain tier: credits needed, no expiry in the loyalty program section"
        values={{ creditsCount: creditsRemaining, tierName, bold: boldTag }}
      />
    } else if (expiryDate) {
      bodyText = <FormattedMessage
        id="PA5XXlFH6uNe9Re2lM6n"
        defaultMessage="You need <bold>{nightsCount, plural, one {# Night} other {# Nights}}</bold> by {expiryDate} to retain these {tierName} benefits:"
        description="Retain tier: nights needed with expiry date in the loyalty section"
        values={{ nightsCount: nightsRemaining, expiryDate: formattedExpiryDate, tierName, bold: boldTag }}
      />
    } else {
      bodyText = <FormattedMessage
        id="3R2pCbloA0FNx3NqUK2a"
        defaultMessage="You need <bold>{nightsCount, plural, one {# Night} other {# Nights}}</bold> to retain these {tierName} benefits:"
        description="Retain tier: nights needed, no expiry in the loyalty program section"
        values={{ nightsCount: nightsRemaining, tierName, bold: boldTag }}
      />
    }
  } else {
    headerText = toUnlockHeader
    if (hasCredits && hasNights && expiryDate) {
      bodyText = <FormattedMessage
        id="shZNyOs1FJ_1q4kkcDBU"
        defaultMessage="You need <bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold> and <bold>{nightsCount, plural, one {# Night} other {# Nights}}</bold> by {expiryDate} to unlock these {tierName} benefits:"
        description="Unlock tier: credits and nights needed with expiry date"
        values={{ creditsCount: creditsRemaining, nightsCount: nightsRemaining, expiryDate: formattedExpiryDate, tierName, bold: boldTag }}
      />
    } else if (hasCredits && hasNights) {
      bodyText = <FormattedMessage
        id="m3__sk11VN4PIULtdWOi"
        defaultMessage="You need <bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold> and <bold>{nightsCount, plural, one {# Night} other {# Nights}}</bold> to unlock these {tierName} benefits:"
        description="Unlock tier: credits and nights needed, no expiry in the loyalty program section"
        values={{ creditsCount: creditsRemaining, nightsCount: nightsRemaining, tierName, bold: boldTag }}
      />
    } else if (hasCredits && expiryDate) {
      bodyText = <FormattedMessage
        id="i6TEoGZE3uqdZou93L-N"
        defaultMessage="You need <bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold> by {expiryDate} to unlock these {tierName} benefits:"
        description="Unlock tier: credits needed with expiry date in the loyalty section"
        values={{ creditsCount: creditsRemaining, expiryDate: formattedExpiryDate, tierName, bold: boldTag }}
      />
    } else if (hasCredits) {
      bodyText = <FormattedMessage
        id="A8zL_9AVxbjIkoCGlVup"
        defaultMessage="You need <bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold> to unlock these {tierName} benefits:"
        description="Unlock tier: credits needed, no expiry in the loyalty program section"
        values={{ creditsCount: creditsRemaining, tierName, bold: boldTag }}
      />
    } else if (expiryDate) {
      bodyText = <FormattedMessage
        id="ClECBKha5goSUxdfUVM4"
        defaultMessage="You need <bold>{nightsCount, plural, one {# Night} other {# Nights}}</bold> by {expiryDate} to unlock these {tierName} benefits:"
        description="Unlock tier: nights needed with expiry date in the loyalty section"
        values={{ nightsCount: nightsRemaining, expiryDate: formattedExpiryDate, tierName, bold: boldTag }}
      />
    } else {
      bodyText = <FormattedMessage
        id="iUASorJf6ZPGcpqGRSEi"
        defaultMessage="You need <bold>{nightsCount, plural, one {# Night} other {# Nights}}</bold> to unlock these {tierName} benefits:"
        description="Unlock tier: nights needed, no expiry in the loyalty program section"
        values={{ nightsCount: nightsRemaining, tierName, bold: boldTag }}
      />
    }
  }

  if (!loyaltyConfig) return null

  const viewAllDetailsLinkText = intl.formatMessage({
    id: 'twSX_HWMgnXarKdaxb9d',
    defaultMessage: 'View all details',
    description: 'Link text to view full tier benefit details in the loyalty section',
  })

  return (
    <CardContainer>
      <CardHeader className={`tier-${tier}`}>
        {showToggle && currentTierName && nextTierName && (
          <HeaderToggle>
            <HeaderToggleOption
              className={`tier-${tier} ${!showUpgradeView ? 'is-active' : 'is-inactive'}`}
              onClick={() => onToggleView?.(false)}
            >
              {intl.formatMessage(
                { id: '9ZN5FHO5pQctKoGZ26Aw', defaultMessage: 'Retain {tierName}', description: 'Toggle option to view current tier retention requirements' },
                { tierName: currentTierName },
              )}
            </HeaderToggleOption>
            <HeaderToggleOption
              className={`tier-${tier} ${showUpgradeView ? 'is-active' : 'is-inactive'}`}
              onClick={() => onToggleView?.(true)}
            >
              {intl.formatMessage(
                { id: 'NilqMdlNHrDTNigNu1IR', defaultMessage: 'Unlock {tierName}', description: 'Toggle option to view next tier upgrade requirements' },
                { tierName: nextTierName },
              )}
            </HeaderToggleOption>
          </HeaderToggle>
        )}
        {!(showToggle && currentTierName && nextTierName) && (
          <HeaderText className={`tier-${tier}`}>
            {headerText}
          </HeaderText>
        )}
      </CardHeader>
      <CardBody>
        <RequirementsText>
          {bodyText}
        </RequirementsText>

        <BenefitsContainer>
          {benefits.map((benefit) => (
            <BenefitItem key={benefit.label}>
              <BenefitIconWrapper className={`tier-${tier}`}>
                {benefit.icon}
              </BenefitIconWrapper>
              <BenefitText>
                {benefit.label}
              </BenefitText>
            </BenefitItem>
          ))}

          {onViewDetails && (
            <>
              <ViewDetailsLinkSmall size="small" weight="bold" underline onClick={onViewDetails}>
                {viewAllDetailsLinkText}
              </ViewDetailsLinkSmall>
              <ViewDetailsLinkMedium size="medium" weight="bold" underline onClick={onViewDetails}>
                {viewAllDetailsLinkText}
              </ViewDetailsLinkMedium>
            </>
          )}
        </BenefitsContainer>
      </CardBody>
    </CardContainer>
  )
}

export default LoyaltyHeroUnlockTierCard
